import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fmtDateIST, presetRange } from "@/lib/dates";
import { Package, PackageCheck, Truck, Undo2, PackagePlus, Building2, Users, Zap } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";

const PRESETS = [
  ["all", "All Time"], ["today", "Today"], ["yesterday", "Yesterday"],
  ["week", "This Week"], ["month", "This Month"], ["prevMonth", "Previous Month"],
  ["year", "This Year"], ["custom", "Custom"],
];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function Kpi({ label, value, sub, icon: Icon, chip, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${chip}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-1.5 text-2xl md:text-3xl font-bold text-slate-900 tnum">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function StatusDistribution({ stats }) {
  const total = Math.max(1, stats.total_orders);
  const segs = [
    ["Picked Up", stats.picked_up, "#2563eb"],
    ["In Transit", stats.in_transit, "#ea580c"],
    ["Delivered", stats.delivered, "#16a34a"],
    ["Returned", stats.returned, "#dc2626"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]" data-testid="status-distribution-card">
      <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-4">Order Status Distribution</h3>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" data-testid="status-distribution-bar">
        {segs.map(([n, v, c]) => v > 0 && (
          <div key={n} style={{ width: `${(v / total) * 100}%`, background: c }} title={`${n}: ${v}`} />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {segs.map(([n, v, c]) => (
          <div key={n} className="flex items-center gap-2" data-testid={`dist-${n.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c }} />
            <span className="text-xs text-slate-600 truncate">{n}</span>
            <span className="ml-auto text-sm font-bold text-slate-900 tnum">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [preset, setPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const p = new URLSearchParams();
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    setStats(null);
    setCharts(null);
    api.get(`/dashboard/stats?${p.toString()}`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/dashboard/charts?${p.toString()}`).then((r) => setCharts(r.data)).catch(() => {});
  }, [range]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") return;
    setRange(key === "all" ? { from: "", to: "" } : presetRange(key));
  };

  const applyCustom = () => {
    if (customFrom && customTo) setRange({ from: customFrom, to: customTo });
  };

  const role = user?.role;
  const deliveredPct = stats && stats.total_orders ? Math.round((stats.delivered / stats.total_orders) * 100) : 0;

  return (
    <div className="space-y-4 md:space-y-5" data-testid="dashboard-page">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-[#101E38] text-white p-6 md:p-8" data-testid="dashboard-banner">
        <div className="absolute -right-12 -top-20 h-56 w-56 rounded-full bg-[#E63946]/25" />
        <div className="absolute right-16 -bottom-10 h-40 w-1.5 rotate-[20deg] bg-[#E63946]" />
        <div className="absolute right-32 -bottom-6 h-28 w-1 rotate-[20deg] bg-white/15" />
        <div className="relative max-w-xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8A92]" data-testid="dashboard-role-label">
            {role} portal
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight" data-testid="dashboard-title">
            {greeting()}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="mt-2 text-sm text-slate-300 leading-relaxed">
            {role === "client"
              ? "Track your company's baggage orders from pickup to delivery."
              : "Here's today's operations snapshot across all client orders."}
          </p>
          {(role === "admin" || role === "staff") && (
            <Link
              to="/orders/new"
              data-testid="dashboard-create-order-button"
              className="mt-4 inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#C8323D] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors duration-150"
            >
              <PackagePlus className="h-4 w-4" /> Create Order
            </Link>
          )}
        </div>
      </div>

      {/* Date filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="dashboard-filters">
        {PRESETS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => applyPreset(key)}
            data-testid={`preset-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ${
              preset === key ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary hover:text-primary"
            }`}
          >
            {label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-end gap-2 flex-wrap" data-testid="custom-range-picker">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From</label>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="custom-date-from" className="h-9 border border-slate-300 rounded-sm px-2 text-sm" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">To</label>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="custom-date-to" className="h-9 border border-slate-300 rounded-sm px-2 text-sm" />
            </div>
            <button onClick={applyCustom} data-testid="custom-apply" className="h-9 px-4 bg-primary text-white text-xs font-semibold rounded-sm hover:bg-[#152D53] transition-colors duration-150">
              Apply
            </button>
          </div>
        )}
      </div>

      {/* KPI cards */}
      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="dashboard-stats">
          <Kpi label="Total Orders" value={stats.total_orders} sub={role !== "client" && !range.from ? `+${stats.today_orders} today` : null} icon={Package} chip="bg-[#1B3A6B]/10 text-[#1B3A6B]" testId="stat-total-orders" />
          <Kpi label="Delivered" value={stats.delivered} sub={`${deliveredPct}% of all orders`} icon={PackageCheck} chip="bg-green-50 text-green-600" testId="stat-delivered" />
          <Kpi label="In Transit" value={stats.in_transit} icon={Truck} chip="bg-orange-50 text-orange-600" testId="stat-in-transit" />
          <Kpi label="Returned" value={stats.returned} sub={stats.picked_up ? `${stats.picked_up} awaiting pickup` : null} icon={Undo2} chip="bg-red-50 text-red-600" testId="stat-returned" />
        </div>
      )}

      {/* Admin meta strip */}
      {role === "admin" && stats && (
        <div className="flex flex-wrap gap-2" data-testid="admin-meta-strip">
          <Link to="/clients" className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 hover:border-primary hover:text-primary transition-colors duration-150" data-testid="meta-clients">
            <Building2 className="h-3.5 w-3.5" /> {stats.total_clients} Clients
          </Link>
          <Link to="/staff" className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 hover:border-primary hover:text-primary transition-colors duration-150" data-testid="meta-staff">
            <Users className="h-3.5 w-3.5" /> {stats.total_staff} Staff
          </Link>
          {charts && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700" data-testid="meta-express">
              <Zap className="h-3.5 w-3.5 text-[#E63946]" /> {charts.by_type.find((t) => t.name === "Express")?.value ?? 0} Express · {charts.by_type.find((t) => t.name === "Normal")?.value ?? 0} Normal
            </span>
          )}
        </div>
      )}

      {/* Status distribution */}
      {stats && <StatusDistribution stats={stats} />}

      {/* Recent orders */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.05)]" data-testid="recent-orders-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Recent Orders</h3>
          <Link to={role === "client" ? "/my-orders" : "/orders"} className="text-xs font-semibold text-primary hover:underline" data-testid="view-all-orders-link">
            View all
          </Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(charts?.recent || []).slice(0, 5).map((o) => (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              data-testid={`recent-order-${o.order_id}`}
              className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-blue-50/40 transition-colors duration-100"
            >
              <span className="font-semibold text-primary text-sm whitespace-nowrap w-24">{o.order_id}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">{o.passenger_name}</span>
                <span className="block text-xs text-slate-500 truncate">
                  {role !== "client" ? `${o.client_name} · ` : ""}{fmtDateIST(o.created_at)}
                </span>
              </span>
              <StatusBadge status={o.delivery_status} />
            </Link>
          ))}
          {charts && charts.recent.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">No orders yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
